import { useId } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { levelTone } from '../../labels';
import type { HealthLevel } from '../../labels';

/**
 * Containers (CO-88, audit C1, C2, D3). There is one bordered container, the
 * `Section`. Sections are never nested; inner structure is a `Subsection`
 * (a title and a divider) or plain spacing. The project area's
 * `PanelCard` + `SectionHeader` are this same markup and remain as aliases.
 */

export function SectionHeader({
  title,
  description,
  help,
  helpLabel = 'About this',
  actions,
  id,
  level = 2,
}: {
  title: ReactNode;
  /** One sentence. Longer explanations go in `help`. */
  description?: ReactNode;
  /** An explanation that is useful the first time and noise afterwards:
   * shown behind a disclosure (audit D3). */
  help?: ReactNode;
  /** Names the disclosure after what it explains ("How matching works",
   * CO-158); "About this" when it is general. */
  helpLabel?: string;
  actions?: ReactNode;
  id?: string;
  level?: 2 | 3;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className="section-header">
      <div className="section-header__text">
        <Heading id={id} className="section-header__title">
          {title}
        </Heading>
        {description ? <p className="section-header__desc">{description}</p> : null}
        {help ? (
          <details className="section-help">
            <summary>{helpLabel}</summary>
            <div className="section-help__body">{help}</div>
          </details>
        ) : null}
      </div>
      {actions ? <div className="section-header__actions">{actions}</div> : null}
    </div>
  );
}

export function Section({
  title,
  description,
  help,
  helpLabel,
  actions,
  children,
  tone,
  testId,
  className,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  help?: ReactNode;
  helpLabel?: string;
  actions?: ReactNode;
  children?: ReactNode;
  /** Tints the left edge for a section whose whole content is a problem. */
  tone?: 'warning' | 'critical';
  testId?: string;
  className?: string;
  id?: string;
}) {
  const generated = useId();
  const titleId = id ?? generated;
  return (
    <section
      className={`panel${tone ? ` panel--${tone}` : ''}${className ? ` ${className}` : ''}`}
      aria-labelledby={title ? titleId : undefined}
      data-testid={testId}
    >
      {title ? <SectionHeader id={titleId} title={title} description={description} help={help} helpLabel={helpLabel} actions={actions} /> : null}
      {children}
    </section>
  );
}

/** A titled part of a Section, separated by a divider rather than a box. */
export function Subsection({
  title,
  description,
  help,
  helpLabel,
  actions,
  children,
  testId,
}: {
  title: ReactNode;
  description?: ReactNode;
  help?: ReactNode;
  helpLabel?: string;
  actions?: ReactNode;
  children?: ReactNode;
  testId?: string;
}) {
  const titleId = useId();
  return (
    <section className="subsection" aria-labelledby={titleId} data-testid={testId}>
      <SectionHeader id={titleId} level={3} title={title} description={description} help={help} helpLabel={helpLabel} actions={actions} />
      {children}
    </section>
  );
}

/**
 * SummaryTile: one cell of a row of summaries (project Overview, the
 * dashboard). A label, one headline fact, one supporting line and at most
 * one link. Tiles in a row share a height; the level tints only the dot
 * and the left edge.
 */
export function SummaryTile({
  label,
  level,
  headline,
  detail,
  meta,
  link,
  testId,
}: {
  label: string;
  /** Omitted for a plain count, which has no health to colour. */
  level?: HealthLevel;
  headline: ReactNode;
  detail?: ReactNode;
  /** A second, quieter fact under the detail. */
  meta?: ReactNode;
  link?: { label: string; to: string };
  testId?: string;
}) {
  return (
    <section className={`summary-card${level ? ` summary-card--${level}` : ''}`} data-testid={testId} aria-label={label} data-tone={level ? levelTone(level) : undefined}>
      <div className="summary-card__label">
        {level ? <span className={`level-dot level-dot--${level}`} aria-hidden="true" /> : null}
        {label}
      </div>
      <div className="summary-card__headline">{headline}</div>
      {detail ? <div className="summary-card__detail">{detail}</div> : null}
      {meta ? <div className="summary-card__meta">{meta}</div> : null}
      {link ? (
        <Link className="summary-card__link" to={link.to}>
          {link.label}
        </Link>
      ) : null}
    </section>
  );
}

/** A row of SummaryTiles: equal heights, as many per row as fit (min 200 px). */
export function SummaryTiles({ children, label, testId }: { children: ReactNode; label?: string; testId?: string }) {
  return (
    <div className="summary-tiles" role={label ? 'group' : undefined} aria-label={label} data-testid={testId}>
      {children}
    </div>
  );
}

